// A Mexican state is stored as its ISO 3166-2 code so that the three spellings that reach us --
// Google's `Gto.`, a TMS's `GTO`, a human's `Guanajuato` -- compare equal. Nobody writes `GUA`
// though, so it is rendered back as the conventional Spanish abbreviation: dotted, and accented
// where the name is.
//
// This table mirrors MexicanStateUtils.toDisplayName in cargochief-commons. The two have to agree;
// a state rendered one way on a screen and another in an email is the mismatch we just removed.
// Commons additionally pins, in its tests, that every name here canonicalises back to the code it
// came from -- so a displayed value that re-enters as a pasted search or a re-uploaded export
// lands on the same state.
export const MEXICAN_STATE_DISPLAY_NAMES: Readonly<Record<string, string>> = Object.freeze({
	AGU: 'Ags.',
	BCN: 'B.C.',
	BCS: 'B.C.S.',
	CAM: 'Camp.',
	CHP: 'Chis.',
	CHH: 'Chih.',
	// Mexico City abbreviates to its own initials, undotted, rather than to a clipped name.
	CMX: 'CDMX',
	COA: 'Coah.',
	COL: 'Col.',
	DUR: 'Dgo.',
	GUA: 'Gto.',
	GRO: 'Gro.',
	HID: 'Hgo.',
	JAL: 'Jal.',
	MEX: 'Méx.',
	MIC: 'Mich.',
	MOR: 'Mor.',
	NAY: 'Nay.',
	NLE: 'N.L.',
	OAX: 'Oax.',
	PUE: 'Pue.',
	QUE: 'Qro.',
	ROO: 'Q.R.',
	SLP: 'S.L.P.',
	SIN: 'Sin.',
	SON: 'Son.',
	TAB: 'Tab.',
	TAM: 'Tamps.',
	TLA: 'Tlax.',
	VER: 'Ver.',
	YUC: 'Yuc.',
	ZAC: 'Zac.',
});

/**
 * Renders a stored state for display: `GUA` reads back as `Gto.`, `NLE` as `N.L.` Anything else --
 * a US state, a Canadian province, a row not yet backfilled -- comes back upper-cased, which is
 * what every location formatter here has always done to a state.
 *
 * Takes no country and must not: every Mexican ISO 3166-2 code is three letters and every US state
 * and Canadian province code is two, so the sets cannot collide. The match is exact for the same
 * reason -- resolving a bare `NL` would relabel every Newfoundland lane as Nuevo León.
 */
export const DISPLAY_STATE = (state?: string | null): string => {
	const code = state?.trim().toUpperCase() ?? '';
	return MEXICAN_STATE_DISPLAY_NAMES[code] ?? code;
};

/**
 * The same, for the comma-joined `destinationStates` a lane carries.
 */
export const DISPLAY_STATES = (states?: string | null): string => {
	if (!states?.trim()) {
		return '';
	}
	return states
		.split(',')
		.map((state) => DISPLAY_STATE(state))
		.filter((state) => state.length > 0)
		.join(',');
};
